import UserSeparation from "@/services/api/UserSeparation";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface Loadingstate {
  checkList: boolean;
  separationModes: boolean;
  separationRecords: boolean;
  separationExport: boolean;
}

interface UserSeparationState {
  checklist: any[];
  separationModes: any[];
  separationRecords: any[];
  separationDetails: any | null;
  error: string | null;
  loading: Loadingstate;
}

const initialState: UserSeparationState = {
  checklist: [],
  separationModes: [],
  separationRecords: [],
  separationDetails: null,
  error: null,
  loading: {
    checkList: false,
    separationModes: false,
    separationRecords: false,
    separationExport: false,
  },
};

export const getCheckList = createAsyncThunk(
  "userSeparation/getCheckList",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.getCheckList(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch checklist");
    }
  },
);

export const addCheckList = createAsyncThunk(
  "userSeparation/addCheckList",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.addCheckList(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add checklist");
    }
  },
);

export const deleteCheckList = createAsyncThunk(
  "userSeparation/deleteCheckList",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.deleteCheckList(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete checklist");
    }
  },
);

export const getSeparationModes = createAsyncThunk(
  "userSeparation/getSeparationModes",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.getSeparationModes(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch separation modes",
      );
    }
  },
);

export const addEmployeeSeparation = createAsyncThunk(
  "userSeparation/addEmployeeSeparation",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.addEmployeeSeparation(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to add employee separation",
      );
    }
  },
);

export const getAllSeparationRecords = createAsyncThunk(
  "userSeparation/getAllSeparationRecords",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.getAllSeparationRecords(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch separation records",
      );
    }
  },
);

export const getEmployeeSeparationDetails = createAsyncThunk(
  "userSeparation/getEmployeeSeparationDetails",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.getEmployeeSeparationDetails(data);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch employee separation details",
      );
    }
  },
);

export const deleteEmployeeSeparation = createAsyncThunk(
  "userSeparation/deleteEmployeeSeparation",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await UserSeparation.deleteEmployeeSeparation(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to delete employee separation",
      );
    }
  },
);

export const separationExport = createAsyncThunk(
  "userSeparation/separationExport",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await UserSeparation.separationExport(payload);
      return saveAsExcelFile(res, "SeparationRecord");
    } catch (err: any) {
      rejectWithValue(err?.response?.data?.message || "Failed to export");
    }
  },
);

const userSeparationSlice = createSlice({
  name: "userSeparation",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCheckList.pending, (state) => {
        state.loading.checkList = true;
        state.checklist = [];
        state.error = null;
      })
      .addCase(getCheckList.fulfilled, (state, action) => {
        state.loading.checkList = false;
        state.checklist = action.payload;
        state.error = null;
      })
      .addCase(getCheckList.rejected, (state, action) => {
        state.loading.checkList = false;
        state.checklist = [];
        state.error = action.payload as string;
      })

      .addCase(getSeparationModes.pending, (state) => {
        state.loading.separationModes = true;
        state.separationModes = [];
        state.error = null;
      })
      .addCase(getSeparationModes.fulfilled, (state, action) => {
        state.loading.separationModes = false;
        state.separationModes = action.payload;
        state.error = null;
      })
      .addCase(getSeparationModes.rejected, (state, action) => {
        state.loading.separationModes = false;
        state.separationModes = [];
        state.error = action.payload as string;
      })

      .addCase(getAllSeparationRecords.pending, (state) => {
        state.loading.separationRecords = true;
        state.separationRecords = [];
        state.error = null;
      })
      .addCase(getAllSeparationRecords.fulfilled, (state, action) => {
        state.loading.separationRecords = false;
        state.separationRecords = action.payload || [];
        state.error = null;
      })
      .addCase(getAllSeparationRecords.rejected, (state, action) => {
        state.loading.separationRecords = false;
        state.separationRecords = [];
        state.error = action.payload as string;
      })

      .addCase(getEmployeeSeparationDetails.fulfilled, (state, action) => {
        state.separationDetails = action.payload;
      })

      .addCase(separationExport.pending, (state) => {
        state.loading.separationExport = true;
        state.error = null;
      })
      .addCase(separationExport.fulfilled, (state) => {
        state.loading.separationExport = false;
      })
      .addCase(separationExport.rejected, (state, action) => {
        state.loading.separationExport = false;
        state.error = action.payload as string;
      });
  },
});

export default userSeparationSlice.reducer;
